<template>
  <v-row>
    <v-col cols="12">
      <v-card>
        <v-card-title>Invoice Customer Info Update</v-card-title>
        <v-card-text>
          <v-row align="end">
            <v-col cols="12" sm="8" md="6">
              <v-text-field
                v-model="lookupInvoiceNo"
                label="Invoice No"
                placeholder="e.g. GCE000123"
                outlined
                dense
                hide-details
                @keyup.enter="loadInvoice"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" md="6">
              <v-btn color="primary" :loading="loading" @click="loadInvoice">
                Load
              </v-btn>
              <v-btn text @click="clearForm" v-if="loaded">
                Clear
              </v-btn>
            </v-col>
          </v-row>

          <template v-if="loaded">
            <v-divider class="my-4"></v-divider>
            <v-row dense>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  label="Invoice No"
                  :value="meta.invoice_no"
                  readonly
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  label="Invoice Date"
                  :value="meta.invoice_datetime"
                  readonly
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  label="Total"
                  :value="meta.total"
                  readonly
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  label="Customer Account Email"
                  :value="meta.customer_account_email"
                  readonly
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-4"></v-divider>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.customer_first_name"
                  label="First Name"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.customer_last_name"
                  label="Last Name"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="form.customer_company_name"
                  label="Company Name"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.customer_address1"
                  label="Address Line 1"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="form.customer_address2"
                  label="Address Line 2 (optional)"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.customer_city"
                  label="City"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.customer_state"
                  label="State (optional)"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.customer_postal_code"
                  label="Postal Code"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="form.customer_country"
                  label="Country"
                  :rules="[rules.required]"
                  dense
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="text-right">
                <v-btn
                  color="primary"
                  :loading="saving"
                  :disabled="!isDirty"
                  @click="save"
                >
                  Save Changes
                </v-btn>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-card>
        <v-card-title>
          Change History
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            label="Search (invoice no, admin email, field name)"
            single-line
            hide-details
            dense
            clearable
            @keyup.enter="loadTable"
            @click:clear="clearSearch"
          ></v-text-field>
        </v-card-title>
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :server-items-length="totalItems"
          :loading="tableLoading"
          :expanded.sync="expanded"
          show-expand
          item-key="id"
          class="elevation-1"
          :footer-props="{
            'items-per-page-options': [30, 60, 100, 150, 250],
          }"
        >
          <template v-slot:item.changed_fields="{ item }">
            {{ renderChangedFields(item.changed_fields) }}
          </template>
          <template v-slot:item.admin_email="{ item }">
            {{ item.admin_email || item.current_admin_email || `#${item.admin_id}` }}
          </template>
          <template v-slot:expanded-item="{ headers: h, item }">
            <td :colspan="h.length">
              <v-row class="py-2">
                <v-col cols="12" md="6">
                  <strong>Old Values</strong>
                  <pre class="json-block">{{ prettyJson(item.old_values) }}</pre>
                </v-col>
                <v-col cols="12" md="6">
                  <strong>New Values</strong>
                  <pre class="json-block">{{ prettyJson(item.new_values) }}</pre>
                </v-col>
                <v-col cols="12" class="caption">
                  IP: {{ item.ip || '-' }} &middot; User-Agent: {{ item.user_agent || '-' }}
                </v-col>
              </v-row>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'InvoiceUpdate',
  data() {
    return {
      lookupInvoiceNo: '',
      loading: false,
      saving: false,
      loaded: false,
      loadedId: null,
      meta: {
        invoice_no: '',
        invoice_datetime: '',
        total: '',
        customer_account_email: '',
      },
      form: this.emptyForm(),
      original: this.emptyForm(),
      rules: {
        required: (value) => (value !== null && value !== undefined && String(value).length > 0) || 'Required.',
      },
      search: '',
      tableLoading: false,
      totalItems: 0,
      items: [],
      expanded: [],
      options: {
        page: 1,
        itemsPerPage: 30,
        sortBy: ['created_at'],
        sortDesc: [true],
      },
      headers: [
        { text: '', value: 'data-table-expand' },
        { text: 'When', value: 'created_at', sortable: true },
        { text: 'Admin', value: 'admin_email', sortable: true },
        { text: 'Invoice No', value: 'invoice_no', sortable: true },
        { text: 'Fields Changed', value: 'changed_fields', sortable: false },
      ],
    };
  },
  computed: {
    isDirty() {
      if (!this.loaded) return false;
      const keys = Object.keys(this.form);
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if ((this.form[k] || '') !== (this.original[k] || '')) return true;
      }
      return false;
    },
  },
  watch: {
    options: {
      handler() {
        this.loadTable();
      },
      deep: true,
    },
  },
  created() {
    this.loadTable();
  },
  methods: {
    emptyForm() {
      return {
        customer_first_name: '',
        customer_last_name: '',
        customer_company_name: '',
        customer_address1: '',
        customer_address2: '',
        customer_state: '',
        customer_city: '',
        customer_country: '',
        customer_postal_code: '',
      };
    },
    loadInvoice() {
      const invoiceNo = (this.lookupInvoiceNo || '').trim();
      if (!invoiceNo) {
        this.$store.dispatch('addAlert', {
          id: Date.now(),
          type: 'error',
          message: 'Please enter an invoice number.',
        });
        return;
      }
      this.loading = true;
      this.$api.post('admin/invoice/get-by-no', { invoice_no: invoiceNo })
        .then((response) => {
          const { invoice, customer_account_email: accountEmail } = response.data;
          this.loadedId = invoice.id;
          this.meta = {
            invoice_no: invoice.invoice_no,
            invoice_datetime: invoice.invoice_datetime,
            total: invoice.total,
            customer_account_email: accountEmail,
          };
          const hydrated = {
            customer_first_name: invoice.customer_first_name || '',
            customer_last_name: invoice.customer_last_name || '',
            customer_company_name: invoice.customer_company_name || '',
            customer_address1: invoice.customer_address1 || '',
            customer_address2: invoice.customer_address2 || '',
            customer_state: invoice.customer_state || '',
            customer_city: invoice.customer_city || '',
            customer_country: invoice.customer_country || '',
            customer_postal_code: invoice.customer_postal_code || '',
          };
          this.form = { ...hydrated };
          this.original = { ...hydrated };
          this.loaded = true;
        })
        .catch((err) => {
          const msg = this.extractErrorMessage(err, 'Failed to load invoice.');
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: msg,
          });
          this.loaded = false;
          this.loadedId = null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      if (!this.loadedId) return;
      this.saving = true;
      this.$api.put(`admin/invoice/${this.loadedId}/customer-info`, this.form)
        .then((response) => {
          if (response.data && response.data.updated) {
            this.$store.dispatch('addAlert', {
              id: Date.now(),
              type: 'success',
              message: `Invoice updated. Changed: ${(response.data.changed_fields || []).join(', ')}`,
            });
            this.original = { ...this.form };
            this.loadTable();
          } else {
            this.$store.dispatch('addAlert', {
              id: Date.now(),
              type: 'info',
              message: 'No changes detected.',
            });
          }
        })
        .catch((err) => {
          const msg = this.extractErrorMessage(err, 'Failed to update invoice.');
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: msg,
          });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    clearForm() {
      this.loaded = false;
      this.loadedId = null;
      this.lookupInvoiceNo = '';
      this.form = this.emptyForm();
      this.original = this.emptyForm();
      this.meta = {
        invoice_no: '',
        invoice_datetime: '',
        total: '',
        customer_account_email: '',
      };
    },
    loadTable() {
      const {
        sortBy, sortDesc, page, itemsPerPage,
      } = this.options;
      const sortKey = (sortBy && sortBy.length > 0) ? sortBy[0] : 'created_at';
      const sortDir = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : true;
      this.tableLoading = true;
      this.$api.post('admin/invoice-update-log/list', {
        search: this.search ? { '*': this.search } : {},
        current_page: page,
        per_page: itemsPerPage,
        order: `${sortKey}:${sortDir}`,
      })
        .then((response) => {
          this.totalItems = response.data.total;
          this.items = response.data.data;
        })
        .catch((err) => {
          const msg = this.extractErrorMessage(err, 'Failed to load change history.');
          this.$store.dispatch('addAlert', {
            id: Date.now(),
            type: 'error',
            message: msg,
          });
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    extractErrorMessage(err, fallback) {
      const data = err && err.response && err.response.data;
      if (!data) return fallback;
      if (typeof data === 'string') return data;
      if (data.message && typeof data.message === 'string') return data.message;
      if (typeof data === 'object') {
        const firstField = Object.keys(data)[0];
        const firstValue = firstField ? data[firstField] : null;
        if (Array.isArray(firstValue) && firstValue.length > 0) {
          return String(firstValue[0]);
        }
      }
      return fallback;
    },
    clearSearch() {
      this.search = '';
      this.loadTable();
    },
    renderChangedFields(raw) {
      const parsed = this.parseJson(raw);
      if (Array.isArray(parsed)) return parsed.join(', ');
      return '';
    },
    parseJson(raw) {
      if (raw === null || raw === undefined) return null;
      if (typeof raw === 'object') return raw;
      try {
        return JSON.parse(raw);
      } catch (e) {
        return raw;
      }
    },
    prettyJson(raw) {
      const parsed = this.parseJson(raw);
      try {
        return JSON.stringify(parsed, null, 2);
      } catch (e) {
        return String(parsed);
      }
    },
  },
};
</script>

<style scoped>
.json-block {
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
